// PhysicsEngine.ts
import { baseWidth, Rect, type BoardModel } from "./BoardModel";
import { WorldSpace } from "./WorldSpace";

export type Vec = { x:number; y:number };

export const clamp = (v: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, v));
export const len = (v: Vec) => Math.hypot(v.x, v.y);
export const add = (a: Vec, b: Vec) => ({ x: a.x + b.x, y: a.y + b.y });
export const mul = (a: Vec, k: number) => ({ x: a.x * k, y: a.y * k });
export function applyFriction(v: Vec, k: number, dt: number): Vec {
  const speed = len(v);
  if (speed < 1e-3) return { x: 0, y: 0 };
  const decel = k * speed;
  const newSpeed = Math.max(0, speed - decel * dt);
  const f = newSpeed / speed;
  return { x: v.x * f, y: v.y * f };
}

export function estimateSpin(points: Vec[]): number {
  if (points.length < 6) return 0;
  let spin = 0;
  for (let i = 2; i < points.length; i++) {
    const a = points[i - 2];
    const b = points[i - 1];
    const c = points[i];
    const ab = { x: b.x - a.x, y: b.y - a.y };
    const bc = { x: c.x - b.x, y: c.y - b.y };
    const cross = ab.x * bc.y - ab.y * bc.x;
    const mag = (Math.hypot(ab.x, ab.y) * Math.hypot(bc.x, bc.y)) || 1;
    spin += cross / mag;
  }
  return clamp(spin * 0.02, -0.8, 0.8);
}

export const baseTiltX = -10;      // constant slight left tilt; jittered per round by controller

export class PhysicsEngine {
  // --- Baseline physics (reset to sane defaults) ---
  frictionK = 0.25;     // rolling resistance
  spinK     = 290;      // swipe spin → lateral drift strength

  tiltX     = baseTiltX;
  tiltDirection = 1;    // 1 = left tilt (player 0), -1 = right tilt (player 1)
  stopSpeed = 10;       // stop threshold (px/s)
  dropSpeed = 390;      // max speed to allow a drop
  speedScale = 0.55;
  amountOfRandomTilt = (Math.random() * 2.4 - 1.2);

  // --- Rim behavior (COLLISION-BASED PHYSICS) ---
  rimInnerFrac       = 0.30;  // inner capture radius (TINY = almost no magnetic pull)
  rimOuterFrac       = 1.2;   // outer annulus where rimming can happen (MASSIVE ZONE)  
  rimStraightCos     = 0.94;  // treat near‑straight approaches as clean sinks (cos(20°) = allow ±20° cone)

  // Apollo 13 tuning: Allow full orbits by preventing early capture during rimming
  rimOrbitMinSpeed   = 30;    // Speed threshold: very low - almost any speed can orbit

  // --- Back-edge pop behavior ---
  lipCooldownMs    = 200;  // min time between back-edge pops (ms)
  private lastLipMs = 0;   // internal cooldown timer

  world!: WorldSpace;

  // sink animation state
  sink = { active:false, cx:0, cy:0, r:0, t:0, dur:0.35, pts:0 };

  // Orbit tracking for Apollo 13 detection
  rimmingFrames = 0;
  lastRimmingHole: { cx: number; cy: number } | null = null;
  lastRimmingAngle: number | null = null;  // Previous frame's angle to calculate frame-to-frame rotation
  cumulativeRotation = 0;  // Total rotation accumulated (can exceed 360°)
  apollo13Detected = false;  // Flag to track if Apollo 13 was detected this roll
  globalFrameCount = 0;  // Increments every step() call
  lastRimmingGlobalFrame = 0;  // Track which global frame we last rimmed on

  // Stats tracking for rimming behavior
  hadAnyRimming = false;  // Did this roll rim at all?
  maxOrbitsThisRoll = 0;  // Max orbits achieved before sink/escape
  popCountThisRoll = 0;   // Number of back-edge pops this roll
  rimmingHoleCenter: { cx: number; cy: number } | null = null;  // Which hole was ball rimming
  finalSinkHoleCenter: { cx: number; cy: number } | null = null;  // Which hole did ball sink into
  didEscape = false;  // Did ball escape after >1 orbit?
  didSink = false;  // Did ball sink after >1 orbit?
  wasRailroad = false;  // Was this roll a railroad incident?
  justEscaped = false;  // Just escaped this frame - skip all capture checks
  rimmingResolved = false;  // Rimming outcome already determined (escape or sink)

  // Bypass tracking - detect when ball passes over hole without sinking
  bypassTracking = new Map<string, boolean | {
    seen: boolean;
    d: number;
    speed: number;
    v_r: number;
    v_t: number;
    cosToCenter: number;
    straightIn: boolean;
    velocityAngle: number;
    positionAngle: number;
    spin: number;
    holeR: number;
    pts: number;
  }>();
  
  // Track previous ball position for path-based collision detection
  lastBallPos: Vec | null = null;
  
  // Track initial roll speed for debugging
  initialRollSpeed: number = 0;
  hasLoggedInitialSpeed: boolean = false;

  // bounce animation state (ball pops up when hitting back edge)
  bounce = { active:false, startPos:{ x:0, y:0 }, baseR:0, t:0, dur:0.4, vel:{ x:0, y:0 } };

  // Track which hole we just popped from - prevent immediate recapture
  justPoppedFromHole: { cx: number; cy: number } | null = null;
  popImmunityFrames = 0;  // Frames of immunity after pop

  constructor(private board: BoardModel,
              public ball: { pos:Vec; vel:Vec; r:number; spin:number; rolling:boolean },
              world: WorldSpace) {
    this.board = board;
    this.ball = ball;
    this.world = world;
  }

  public stop() {
    // Stop transient animations/states that would continue updating between games
    this.sink.active = false;
    this.bounce.active = false;
    this.ball.rolling = false;
    this.lastBallPos = null;
  }

  resetTilt() {
    //baseTiltX = base;
    this.tiltX = baseTiltX * this.tiltDirection;
  }
  jitterTilt() {
    // Add random jitter: ±1.2 range around baseTiltX
    this.amountOfRandomTilt = (Math.random() * 2.4 - 1.2);
    this.tiltX = baseTiltX * this.tiltDirection + this.amountOfRandomTilt;
  }

  setTiltDirection(playerIndex: number) {
    // Player 0 gets left tilt (direction = 1), Player 1 gets right tilt (direction = -1)
    this.tiltDirection = playerIndex === 0 ? 1 : -1;
  }

  step(dt:number,
       ctx:{onDitch:()=>void; onStopped:()=>void; onSink:(cx:number,cy:number,r:number,pts:number)=>void;
  }) {
    this.globalFrameCount++;  // Track physics frame number
    const b = this.ball;
    this.world.setAspect(this.board.aspect);
    this.world.framePad = this.board.framePad;
    this.world.moatPad = this.board.moatPad;
    this.world.playPad = this.board.playPad;
    this.world.slabHeightRatio = this.board.slabHeightRatio;
    const { play, slab } = this.world.worldRects();

    // Reset orbit tracking when ball stops rolling (for next roll)
    if (!b.rolling && this.rimmingFrames > 0) {
      this.rimmingFrames = 0;
      this.lastRimmingHole = null;
      this.lastRimmingAngle = null;
      this.cumulativeRotation = 0;
      this.apollo13Detected = false;
      this.hadAnyRimming = false;
      this.maxOrbitsThisRoll = 0;
      this.rimmingHoleCenter = null;
      this.finalSinkHoleCenter = null;
      this.didEscape = false;
      this.didSink = false;
      this.wasRailroad = false;
      this.justEscaped = false;
      this.rimmingResolved = false;
      this.bypassTracking.clear();  // Reset bypass tracking for next roll
      this.lastBallPos = null;  // Reset path tracking
      this.hasLoggedInitialSpeed = false;  // Reset speed logging
      this.justPoppedFromHole = null;  // Reset pop immunity
      this.popImmunityFrames = 0;
    }

    // ---- bounce animation (ball pops up after hitting back edge) ----
    if (this.bounce.active) {
      this.bounce.t += dt / this.bounce.dur;
      const t = clamp(this.bounce.t, 0, 1);

      // Parabolic arc: grow then shrink (simulates rising and falling)
      // Peak at t=0.5, using sin wave for smooth arc
      const arc = Math.sin(t * Math.PI); // 0 -> 1 -> 0
      b.r = this.bounce.baseR * (1 + arc * 0.6); // Grows up to 60% larger at peak

      // Ball continues moving during bounce (with reduced velocity)
      b.pos.x = this.bounce.startPos.x + this.bounce.vel.x * t * this.bounce.dur;
      b.pos.y = this.bounce.startPos.y + this.bounce.vel.y * t * this.bounce.dur;

      if (t >= 1) {
        // Animation done - resume rolling with the landing velocity (with scatter applied)
        this.bounce.active = false;
        b.r = this.bounce.baseR;
        b.vel = this.bounce.vel;  // Use landing velocity if available
        b.rolling = true;
      }
      return;
    }

    // ---- sink animation ----
    if (this.sink.active) {
      this.sink.t += dt / this.sink.dur;
      const t = clamp(this.sink.t, 0, 1);
      const e = t * t; // ease-in
      b.pos.x = this.sink.cx; b.pos.y = this.sink.cy; b.r = Math.max(1, this.sink.r * (1 - e));
      if (t >= 1) {
        this.sink.active = false;
        // Distinguish between actual sinks (scoring holes) and ditches (moat falls)
        if (this.sink.pts > 0) {
          ctx.onSink(this.sink.cx, this.sink.cy, this.sink.r, this.sink.pts);
        } else {
          ctx.onDitch();
        }
      }
      return;
    }

    if (!b.rolling) return;


    // Decrement pop immunity counter
    if (this.popImmunityFrames > 0) {
      this.popImmunityFrames--;
      if (this.popImmunityFrames === 0) {
        this.justPoppedFromHole = null;  // Clear immunity
      }
    }

    // ---- integrate motion (spin drift + tilt + friction) ----
    const drift = b.spin * (len(b.vel) / 200) * this.spinK;
    b.vel.x += drift * dt;
    b.vel.x += this.tiltX * dt;

    // Check if ball is in rimming zone BEFORE applying friction
    // We'll apply rim-specific friction later if needed
    let isRimming = false;
    const holes = this.board.holesAbs(this.world.worldRects().slab);
    for (const h of holes) {
      const dx = b.pos.x - h.cx, dy = b.pos.y - h.cy;
      const d = Math.hypot(dx, dy);
      const rimRadius = h.r * 0.85;
      const contactThreshold = 0.25;
      const distanceFromRim = Math.abs(d - rimRadius);
      if (distanceFromRim < h.r * contactThreshold) {
        isRimming = true;
        break;
      }
    }

    // Only apply ground friction if NOT rimming
    if (!isRimming) {
      b.vel = applyFriction(b.vel, this.frictionK, dt);
    }

    b.pos = add(b.pos, mul(b.vel, dt));

    // ---- slab edge: fall into moat with sink animation (0 pts) ----  
    // Use moat boundaries since that's what the user sees visually as the boundary
    const { moat } = this.world.worldRects();
    const leftHit   = (b.pos.x - b.r) <= moat.x;
    const rightHit  = (b.pos.x + b.r) >= (moat.x + moat.w);
    const topHit    = (b.pos.y - b.r) <= moat.y;
    const bottomHit = (b.pos.y + b.r) >= (moat.y + moat.h);
    
    
    if (leftHit || rightHit || topHit || bottomHit) {
      let cx = b.pos.x, cy = b.pos.y;
      if (leftHit)   { cx = moat.x;                cy = clamp(b.pos.y, moat.y, moat.y + moat.h); }
      if (rightHit)  { cx = moat.x + moat.w;       cy = clamp(b.pos.y, moat.y, moat.y + moat.h); }
      if (topHit)    { cx = clamp(b.pos.x, moat.x, moat.x + moat.w); cy = moat.y; }
      if (bottomHit) { cx = clamp(b.pos.x, moat.x, moat.x + moat.w); cy = moat.y + moat.h; }
      this.sink = { active:true, cx, cy, r:b.r, t:0, dur:0.28, pts:0 };
      return; // controller will treat onSink(...,0) as 0 points and advance
    }

        // ---- hole interaction ----
    //const holes = this.board.holesAbs(slab);
    const nowMs = performance.now();
    for (const h of holes) {
      const dx = b.pos.x - h.cx, dy = b.pos.y - h.cy;
      const d  = Math.hypot(dx, dy);
      const holeR = h.r;

      // Skip capture checks for hole we just popped from (temporary immunity)
      const isImmuneHole = this.justPoppedFromHole &&
                          h.cx === this.justPoppedFromHole.cx &&
                          h.cy === this.justPoppedFromHole.cy;

      const speed = Math.max(1e-6, len(b.vel));
      const slow  = speed < this.dropSpeed;

      // Speed threshold for capture - scales with hole size (bigger holes = higher threshold)
      const speedThreshold = 250 + (holeR / 20) * 100;

      // Decision radii (tighter to actual hole geometry)
      const inner = this.rimInnerFrac * holeR;  // inner capture zone
      const edge  = holeR - b.r * 0.1;          // edge detection when ball center is near hole edge
      const outer = holeR + b.r * 0.7;          // rim contact zone (wider for Apollo 13 orbits)

      // Local polar frame
      const invD = d > 0 ? 1 / d : 0;
      const urx = d > 0 ? dx * invD : 0; // radial outward
      const ury = d > 0 ? dy * invD : 0;
      const utx = -ury, uty = urx;       // tangent (CCW)
      const v_r = b.vel.x * urx + b.vel.y * ury; // outward(+)/inward(-)
      const v_t = b.vel.x * utx + b.vel.y * uty; // tangential
      const cosToCenter = clamp((-v_r) / speed, -1, 1);
      const straightIn  = cosToCenter >= this.rimStraightCos;

      // ----------------------------
      // RIMMING: Physical rim contact (ball rolling along circular edge)
      // ----------------------------
      // Real physics: Ball drops into hole, continues forward momentum
      // IF trajectory takes it to the rim edge, it makes continuous contact
      // Contact creates normal force (keeps ball on circular path) + friction (slows it down)

      // Rimming: Ball is near rim edge with tangential velocity
      const rimRadius = holeR * 0.85; // Where the rim edge is located
      const contactThreshold = 0.35; // Contact zone width (35% of hole radius - catch grazing balls)
      const distanceFromRim = Math.abs(d - rimRadius);
      const inContactWithRim = distanceFromRim < holeR * contactThreshold;

      // Need tangential velocity to orbit (moving around the hole, not just toward/away)
      const hasOrbitalMotion = Math.abs(v_t) > 50; // Raised from 25 - need decent curve to rim

      // ANTI-RAILROAD: Don't rim if ball is heading mostly toward center
      // If radial velocity (inward) dominates tangential, ball should sink, not rim
      const headingTowardCenter = v_r < 0 && Math.abs(v_r) > Math.abs(v_t) * 0.7;

      // Don't rim if ball is WAY too fast - it should either pop or bypass
      const tooFastToRim = speed > speedThreshold * 1.3; // 30% above sink threshold

      // Don't rim if ball is deep inside hole - it should sink instead
      // Balls skimming near rim edge can rim, but balls deep inside should fall in
      const tooDeepToRim = d < holeR * 0.75;  // Ball center is within 75% of hole radius (25% penetration or deeper)

      if (inContactWithRim && hasOrbitalMotion && !headingTowardCenter && !tooFastToRim && !tooDeepToRim) {
        // Track "railroad" captures: ball moving pretty straight toward hole but caught by rim
        if (this.rimmingFrames === 0 && straightIn && Math.abs(v_t) < 100) {
          this.wasRailroad = true;
        }

        // Ball hit the far rim edge while trying to exit - redirect into orbit
        const targetRadius = rimRadius;

        // Gentle position nudge toward rim - but only if not escaping or very slow
        // If ball has built up escape velocity OR slowed to crawl, let physics take over
        const isCrawling = speed < 60;  // Very slow - let it spiral in naturally
        if (v_r < 50 && !isCrawling) {  // Only correct position if orbiting normally
          const positionCorrection = 0.3; // Very gentle pull
          const radiusError = d - targetRadius;
          const nudgeStrength = radiusError * positionCorrection;

          b.pos.x -= urx * nudgeStrength;
          b.pos.y -= ury * nudgeStrength;
        }

        // Rim contact physics: Balance holding vs escaping
        // If radial velocity is high enough, ball is escaping - don't interfere!
        const isEscaping = v_r >= 100; // Escape threshold (was 120)

        if (v_r > 5 && !isEscaping) {
          // Ball pushing outward but not fast enough to escape - redirect to follow rim
          const currentSpeed = Math.hypot(v_r, v_t);
          const tangentialSign = v_t >= 0 ? 1 : -1;

          // Target ratio: 93% tangential, 12% radial (balanced for multi-orbit stability)
          const targetTangentialFrac = 0.93;
          const targetRadialFrac = 0.12;
          const normalizeFactor = 1 / Math.hypot(targetTangentialFrac, targetRadialFrac);

          const newTangential = currentSpeed * targetTangentialFrac * normalizeFactor * tangentialSign;
          const newRadial = currentSpeed * targetRadialFrac * normalizeFactor;

          b.vel.x = urx * newRadial + utx * newTangential;
          b.vel.y = ury * newRadial + uty * newTangential;
        }

        // Apply friction to slow ball down during rimming (unless escaping)
        if (!isEscaping) {
          const rimFrictionCoeff = 0.12; // 12% friction per second
          const frictionFactor = 1 - (rimFrictionCoeff * dt);
          b.vel.x *= frictionFactor;
          b.vel.y *= frictionFactor;

          // Gentle inward pull - but not if ball is escaping!
          const inwardPull = 8; // Constant gentle pull (fights the 12% radial component)
          b.vel.x -= urx * inwardPull * dt;
          b.vel.y -= ury * inwardPull * dt;
        }

        // Capture logic: Make it feel random like real skeeball
        const newSpeed = len(b.vel);
        const orbitCount = this.cumulativeRotation / (Math.PI * 2);

        // Track stats
        this.hadAnyRimming = true;
        this.maxOrbitsThisRoll = Math.max(this.maxOrbitsThisRoll, orbitCount);

        // Store which hole we're rimming for Apollo 13 detection
        if (!this.rimmingHoleCenter) {
          this.rimmingHoleCenter = { cx: h.cx, cy: h.cy };
        }

        // Calculate "sink probability" that increases over time and as ball slows
        const speedFactor = Math.max(0, (150 - newSpeed) / 150);
        const orbitFactor = Math.min(1, orbitCount / 5);

        // Check for sink/escape every 15 frames
        if (this.rimmingFrames % 15 === 0) {
          const sinkChance = (speedFactor * 0.4 + orbitFactor * 0.6);

          // Escape chance: increases with orbit count and speed
          // Fast balls with many orbits should escape more often
          const escapeSpeedFactor = Math.min(1, newSpeed / 200); // Faster = more likely to escape
          const escapeOrbitFactor = Math.min(1, orbitCount / 3); // More orbits = more likely to escape
          const escapeChance = (escapeSpeedFactor * 0.3 + escapeOrbitFactor * 0.7) * 0.10; // Max 10% escape chance

          const sinkRoll = Math.random();
          const escapeRoll = Math.random();

          const shouldSink = sinkRoll < sinkChance;
          const shouldEscape = escapeRoll < escapeChance;

          // Escape takes priority over sink (if both roll true, escape wins)
          if (shouldEscape && orbitCount > 0.5) {
            if (orbitCount > 1.0) {
              this.didEscape = true;
            }

            // Mark rimming as resolved - don't allow sinking after this
            this.rimmingResolved = true;

            // Give ball a burst of outward velocity to break free
            const escapeBoost = 80; // Outward velocity boost
            b.vel.x += urx * escapeBoost;
            b.vel.y += ury * escapeBoost;

            // Set flag to skip ALL capture checks this frame
            this.justEscaped = true;

            // Exit rimming logic - ball will naturally leave the zone
            break;
          }

          if (shouldSink && orbitCount > 0.5) {
            this.finalSinkHoleCenter = { cx: h.cx, cy: h.cy };
            if (orbitCount > 1.0) {
              this.didSink = true;
            }

            // Clear bypass tracking - ball is sinking
            const holeKey = `${h.cx},${h.cy}`;
            this.bypassTracking.delete(holeKey);

            this.sink = { active:true, cx:h.cx, cy:h.cy, r:b.r, t:0, dur:0.35, pts:h.pts };
            return;
          }
        }

        // Failsafe: Always sink after ridiculous number of orbits
        if (orbitCount > 8) {
          this.finalSinkHoleCenter = { cx: h.cx, cy: h.cy };
          this.didSink = true;
          // Clear bypass tracking - ball is sinking
          const holeKey = `${h.cx},${h.cy}`;
          this.bypassTracking.delete(holeKey);
          this.sink = { active:true, cx:h.cx, cy:h.cy, r:b.r, t:0, dur:0.35, pts:h.pts };
          return;
        }

        // Track rimming frames for Apollo 13 detection
        if (!this.lastRimmingHole || (this.lastRimmingHole.cx === h.cx && this.lastRimmingHole.cy === h.cy)) {
          // Calculate angle from hole center
          const currentAngle = Math.atan2(dy, dx);

          // Check if there was a gap (ball left rimming zone for 2+ frames)
          const framesSinceLastRim = this.globalFrameCount - this.lastRimmingGlobalFrame;
          const hadGap = framesSinceLastRim > 2;

          if (this.rimmingFrames === 0 || hadGap) {
            // Ball left rimming zone - reset tracking for new rimming attempt
            // (Don't log escape here - probabilistic escape handles that)
            this.lastRimmingAngle = currentAngle;
            this.cumulativeRotation = 0;
            this.rimmingFrames = 1;
            this.lastRimmingHole = { cx: h.cx, cy: h.cy };
            this.lastRimmingGlobalFrame = this.globalFrameCount;
          } else {
            // Continuous rimming
            this.rimmingFrames++;
            this.lastRimmingGlobalFrame = this.globalFrameCount;

            // Calculate frame-to-frame angular change
            if (this.lastRimmingAngle !== null) {
              let angleDelta = currentAngle - this.lastRimmingAngle;
              while (angleDelta > Math.PI) angleDelta -= 2 * Math.PI;
              while (angleDelta < -Math.PI) angleDelta += 2 * Math.PI;

              this.cumulativeRotation += Math.abs(angleDelta);
              this.lastRimmingAngle = currentAngle;
            }
          }
        } else {
          // Switched to different hole - reset counter
          this.rimmingFrames = 1;
          this.lastRimmingHole = { cx: h.cx, cy: h.cy };
          this.lastRimmingAngle = Math.atan2(dy, dx);
          this.cumulativeRotation = 0;
        }

        // Skip capture checks this frame - let ball rim
        continue;
      }

      // Skip all capture checks if ball just escaped OR if rimming already resolved
      if (this.justEscaped || this.rimmingResolved) {
        continue;
      }

      // ----------------------------
      // BYPASS TRACKING: Path-based collision detection
      // ----------------------------
      // Check if ball's PATH crossed through or near the hole
      // This catches fast balls that skip between frames
      const holeKey = `${h.cx},${h.cy}`;

      // Current frame: check if ball edge touches hole
      const ballEdgeTouchesHole = d < (holeR + b.r);

      // Path-based: check if ball traveled through hole between frames
      let pathCrossedHole = false;
      let closestPathDist = d;  // Track closest path approach for debugging
      if (this.lastBallPos) {
        // Calculate closest point on path to hole center
        const pathDx = b.pos.x - this.lastBallPos.x;
        const pathDy = b.pos.y - this.lastBallPos.y;
        const pathLength = Math.hypot(pathDx, pathDy);

        if (pathLength > 0.1) {  // Only check if ball actually moved
          // Vector from last position to hole center
          const toHoleDx = h.cx - this.lastBallPos.x;
          const toHoleDy = h.cy - this.lastBallPos.y;

          // Project hole center onto path line
          const t = (toHoleDx * pathDx + toHoleDy * pathDy) / (pathLength * pathLength);
          const tClamped = Math.max(0, Math.min(1, t));  // Clamp to line segment

          // Closest point on path to hole
          const closestX = this.lastBallPos.x + pathDx * tClamped;
          const closestY = this.lastBallPos.y + pathDy * tClamped;

          // Distance from closest point to hole center
          const closestDist = Math.hypot(closestX - h.cx, closestY - h.cy);
          closestPathDist = Math.min(closestDist, d);  // Track minimum distance

          // Did path come within collision distance?
          pathCrossedHole = closestDist < (holeR + b.r);
        }
      }

      if (ballEdgeTouchesHole || pathCrossedHole) {
        const existing = this.bypassTracking.get(holeKey);

        // If not tracked yet OR this is closer than previous, update the tracking
        if (!existing || (typeof existing === 'object' && d < existing.d)) {
          // Store the closest approach to this hole
          this.bypassTracking.set(holeKey, {
            seen: true,
            d,
            speed,
            v_r,
            v_t,
            cosToCenter,
            straightIn,
            velocityAngle: Math.atan2(b.vel.y, b.vel.x) * (180 / Math.PI),
            positionAngle: Math.atan2(dy, dx) * (180 / Math.PI),
            spin: b.spin,
            holeR,
            pts: h.pts
          });
        }
      }

      // Calculate ball edge position for all capture checks
      const ballEdge = d - b.r;  // Distance from ball edge to hole center

      // ----------------------------
      // A) Clean straight-in capture
      // ----------------------------
      // Only capture balls moving straight in that are VERY close to center
      // Balls near the edge should rim instead
      if (!isImmuneHole && d < inner && straightIn && slow) {
        this.finalSinkHoleCenter = { cx: h.cx, cy: h.cy };
        if (this.maxOrbitsThisRoll > 1.0) {
          this.didSink = true;
        }
        // Clear bypass tracking - ball is sinking
        this.bypassTracking.delete(holeKey);
        this.sink = { active:true, cx:h.cx, cy:h.cy, r:b.r, t:0, dur:0.35, pts:h.pts };
        return;
      }

      // ----------------------------
      // B) Very slow crawling capture
      // ----------------------------
      // Only capture if ball is crawling very slowly and close to center
      if (!isImmuneHole && d < holeR * 0.6 && speed < 50) {
        this.finalSinkHoleCenter = { cx: h.cx, cy: h.cy };
        if (this.maxOrbitsThisRoll > 1.0) {
          this.didSink = true;
        }
        // Clear bypass tracking - ball is sinking
        this.bypassTracking.delete(holeKey);
        this.sink = { active:true, cx:h.cx, cy:h.cy, r:b.r, t:0, dur:0.35, pts:h.pts };
        return;
      }

      // ----------------------------
      // C) Direction-agnostic capture for balls deep inside hole
      // ----------------------------
      // On a flat board, balls traveling over the hole should fall in due to gravity
      // Direction doesn't matter - if you're deep inside the hole, you sink
      // Only skip if going VERY fast (would pop or skip across in real life)

      // More aggressive capture zone - balls further in can sink even if going faster
      const captureDepth = d < holeR * 0.65;  // 65% inside hole (was 60%)
      const speedLimit = speedThreshold * 1.45;  // 45% above base threshold (e.g., ~455 px/s for 3pt hole)
      const canCapture = speed < speedLimit;

      // Also check if ball's PATH passed through the capture zone (catches fast balls tunneling)
      // This uses the same path-to-circle distance calculation as bypass tracking
      let pathCrossedCaptureZone = false;
      let closestPathDistToCapture = d;
      if (this.lastBallPos) {
        const pathDx = b.pos.x - this.lastBallPos.x;
        const pathDy = b.pos.y - this.lastBallPos.y;
        const pathLength = Math.hypot(pathDx, pathDy);

        if (pathLength > 0.1) {  // Only check if ball actually moved
          // Vector from last position to hole center
          const toHoleDx = h.cx - this.lastBallPos.x;
          const toHoleDy = h.cy - this.lastBallPos.y;

          // Project hole center onto path line
          const t = (toHoleDx * pathDx + toHoleDy * pathDy) / (pathLength * pathLength);
          const tClamped = Math.max(0, Math.min(1, t));  // Clamp to line segment

          // Closest point on path to hole center
          const closestX = this.lastBallPos.x + pathDx * tClamped;
          const closestY = this.lastBallPos.y + pathDy * tClamped;

          // Distance from closest point to hole center
          const closestDist = Math.hypot(closestX - h.cx, closestY - h.cy);
          closestPathDistToCapture = closestDist;

          // Did path pass through the capture zone?
          const captureRadius = holeR * 0.65;
          if (closestDist < captureRadius) {
            pathCrossedCaptureZone = true;
          }
        }
      }


      if (!isImmuneHole && (captureDepth || pathCrossedCaptureZone) && canCapture) {
        this.finalSinkHoleCenter = { cx: h.cx, cy: h.cy };
        if (this.maxOrbitsThisRoll > 1.0) {
          this.didSink = true;
        }
        // Clear bypass tracking - ball is sinking
        this.bypassTracking.delete(holeKey);
        this.sink = { active:true, cx:h.cx, cy:h.cy, r:b.r, t:0, dur:0.35, pts:h.pts };
        return;
      }

      // Fallback: balls inside hole edge at moderate speed should always sink
      // This catches balls that slip between rimming and capture zones
      const insideHoleEdge = d < holeR * 0.7;  // 70% inside hole
      const moderateSpeed = speed < speedLimit * 1.1;  // Slightly above capture limit

      if (!isImmuneHole && insideHoleEdge && moderateSpeed) {
        this.finalSinkHoleCenter = { cx: h.cx, cy: h.cy };
        if (this.maxOrbitsThisRoll > 1.0) {
          this.didSink = true;
        }
        // Clear bypass tracking - ball is sinking
        this.bypassTracking.delete(holeKey);
        this.sink = { active:true, cx:h.cx, cy:h.cy, r:b.r, t:0, dur:0.35, pts:h.pts };
        return;
      }

      // Additional fallback: balls inside hole but exiting very slowly
      // These are rimming balls that lost momentum and should fall in
      const exitingSlowly = v_r > 0 && v_r < 60;  // Exiting but barely (< 60 px/s radial)
      const insideButNotDeep = d < holeR * 0.85 && d >= holeR * 0.5;  // 15-50% inside

      if (!isImmuneHole && insideButNotDeep && exitingSlowly && speed < speedLimit * 0.9) {
        this.finalSinkHoleCenter = { cx: h.cx, cy: h.cy };
        if (this.maxOrbitsThisRoll > 1.0) {
          this.didSink = true;
        }
        // Clear bypass tracking - ball is sinking
        this.bypassTracking.delete(holeKey);
        this.sink = { active:true, cx:h.cx, cy:h.cy, r:b.r, t:0, dur:0.35, pts:h.pts };
        return;
      }

      // ----------------------------------------------------------
      // D) Back-edge lip pop (ball too fast to sink)
      // ----------------------------------------------------------
      // Ball travels upward (negative Y), back edge is at top of hole
      // When fast ball crosses most of the hole and is exiting, it hits back edge and pops UP
      // This catches balls too fast to sink but deep in the hole

      // Pop should trigger when ball has EXITED past center and is heading toward back edge
      // We want the ball to go: enter → reach deepest point → exit partway back → THEN pop
      // This makes it look like hitting the back lip, not a trampoline in center

      // CRITICAL: Ball must be inside hole for ANY pop to trigger
      const insideHole = d < holeR;

      // Scale pop zone based on hole size - smaller holes need earlier trigger
      // 5pt hole (8.5px): 20-60% penetration, 3pt hole (13px): 25-65%, 1pt hole (19px): 30-70%
      const popZoneInner = holeR * (0.2 + (holeR / 100) * 0.5);  // Smaller holes = wider zone
      const popZoneOuter = holeR * (0.6 + (holeR / 100) * 0.5);
      const exitingNearEdge = d > popZoneInner && d < popZoneOuter;  // Ball is on way OUT
      const tooFastToSink = speed > speedLimit;  // Just above the capture speed limit (e.g., ~448 px/s for 3pt hole)

      // Alternative pop condition: ball rimming at VERY high tangential speed (sideways motion)
      // Only trigger if tangential velocity is extremely high AND ball is near rim edge (not center)
      const nearRimEdge = d > holeR * 0.6 && d < holeR * 0.9;  // Ball is 10-40% penetration (near edge, inside hole)
      const rimmingTooFast = Math.abs(v_t) > speedThreshold * 1.5 && speed > speedThreshold * 1.5 && nearRimEdge;

      // Back-edge pop triggers when:
      // 1. Ball is EXITING and between 30-60% penetration (has bounced back from deepest point)
      // 2. Ball is moving outward (v_r > 100) - heading toward back edge with momentum
      // 3. Ball is too fast to sink
      // 4. OR ball is rimming at extreme speed near rim edge
      const exitingTowardBackEdge = v_r > 100;  // Need significant outward velocity (not just turning around)

      const shouldPop = insideHole && (
                        (exitingNearEdge && tooFastToSink && exitingTowardBackEdge) ||
                        rimmingTooFast
                        );

      if (shouldPop && (nowMs - this.lastLipMs) > this.lipCooldownMs) {
        // Ball pops up and lands with ~10% energy, with significant random scatter
        this.popCountThisRoll++;
        const energyRetained = 0.1;
        const randomAngle = (Math.random() - 0.5) * 1.4; // ±0.7 radians (~±40°) scatter

        // Rotate velocity for scatter effect and reduce energy
        const cos = Math.cos(randomAngle);
        const sin = Math.sin(randomAngle);

        // Add random lateral kick to ensure scatter even for straight-up velocities
        const lateralKick = (Math.random() - 0.5) * 80; // ±40 px/s random sideways push

        const bounceVel = {
          x: (b.vel.x * cos - b.vel.y * sin) * energyRetained + lateralKick,
          y: (b.vel.x * sin + b.vel.y * cos) * energyRetained
        };

        // Start bounce animation - ball travels during the bounce
        this.bounce = {
          active: true,
          startPos: { x: b.pos.x, y: b.pos.y },
          baseR: b.r,
          t: 0,
          dur: 0.4,
          vel: bounceVel,
        };

        b.rolling = false;
        this.lastLipMs = nowMs;

        // Grant immunity from this hole for 30 frames after pop (~0.5 seconds)
        this.justPoppedFromHole = { cx: h.cx, cy: h.cy };
        this.popImmunityFrames = 30;

        return; // Animation takes over
      }

      // Rimming logic moved to top of hole interaction (before capture checks)
    }

    // Store current position for next frame's path detection
    this.lastBallPos = { x: b.pos.x, y: b.pos.y };

    // Reset justEscaped flag after processing all holes
    this.justEscaped = false;

    // ---- stopped in field ----
    if (len(b.vel) < this.stopSpeed) { b.vel = { x:0, y:0 }; b.rolling = false; ctx.onStopped(); return; }
  }

  // Log bypasses at end of roll (called when ball stops or ditches)
  logBypassesForCompletedRoll() {
    // Bypass logging disabled - can be re-enabled for physics debugging
  }
}
